import { useCallback, useEffect, useMemo, useState } from 'react';
import CustomButton from '../../button/button';
import { availableLanguages } from '../../data/languageData';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import successToast from '../../toasts/successToast';
import errorToast from '../../toasts/errorToast';
import { getPDFTextBlocksAction, updatePDFFileAction } from '../../../redux/actions/translationAction';
import { Spinner, Table } from 'react-bootstrap';
import PdfTextBlock from './pdf-text-block';
import PdfSourceTextBlock from './pdf-source-text';

const PdfEditor = ({ documentData, sourceFileUrl, translatedFileUrl, job_id, isLoading }) => {
    const [docxTextBlocks, setDocxTextBlocks] = useState([]);
    const [translatedTextBlocks, setTranslatedTextBlocks] = useState([]);
    const [sourceTextBlocks, setSourceTextBlocks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editedHistory, setEditedHistory] = useState({}); // Changed from Map to object
    const [selectedId, setSelectedId] = useState(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        if (translatedFileUrl) {
            setLoading(true);
            dispatch(getPDFTextBlocksAction({ job_id, sourceFileUrl }, onFetchSuccess, onError));
        }
    }, [translatedFileUrl]);

    const groupByParaId = (textBlocks) => {
        const paraMap = new Map();
        textBlocks.forEach(block => {
            if (!paraMap.has(block.paraId)) {
                paraMap.set(block.paraId, []);
            }
            paraMap.get(block.paraId).push(block);
        });
        return Array.from(paraMap.values());
    };

    const groupSentences = (textBlocks) => {
        const groups = [];
        let currentGroup = [];
        let currentParaId = null;
        let currentSentId = null;

        for (const block of textBlocks) {
            if (block.paraId !== currentParaId || block.sentId !== currentSentId) {
                if (currentGroup.length > 0) {
                    groups.push(currentGroup);
                }
                currentGroup = [block];
                currentParaId = block.paraId;
                currentSentId = block.sentId;
            } else {
                currentGroup.push(block);
            }
        }

        if (currentGroup.length > 0) {
            groups.push(currentGroup);
        }

        return groups;
    };

    const groupedTranslatedSentences = useMemo(() => {
        return groupSentences(translatedTextBlocks);
    }, [translatedTextBlocks]);

    const groupedSourceSentences = useMemo(() => {
        return groupSentences(sourceTextBlocks);
    }, [sourceTextBlocks]);

    const getNormalizedGroups = () => {
        if (groupedSourceSentences.length === groupedTranslatedSentences.length) {
            return {
                source: groupedSourceSentences,
                translated: groupedTranslatedSentences
            };
        }
        return {
            source: groupByParaId(sourceTextBlocks),
            translated: groupByParaId(translatedTextBlocks)
        };
    };

    const normalizedGroups = useMemo(() => getNormalizedGroups(),
        [groupedSourceSentences, groupedTranslatedSentences, sourceTextBlocks, translatedTextBlocks]);


    const onFetchSuccess = (data) => {
        setDocxTextBlocks(data.data.data.translatedTextBlocks);
        setTranslatedTextBlocks(data.data.data.translatedTextBlocks);
        setSourceTextBlocks(data.data.data.sourceTextBlocks);
        setLoading(false);
    };

    const handleTextBlockUpdate = useCallback((index, updatedBlocks) => {
        setDocxTextBlocks(prevBlocks => {
            // Create a new array with all blocks
            const newBlocks = [...prevBlocks];

            // Update each block that was changed
            updatedBlocks.forEach(updatedBlock => {
                const existingIndex = newBlocks.findIndex(b => b.id === updatedBlock.id);
                if (existingIndex >= 0) {
                    // Replace the entire block to ensure updates
                    newBlocks[existingIndex] = { ...updatedBlock };
                } else {
                    // Add new block if it didn't exist
                    newBlocks.push({ ...updatedBlock });
                }
            });

            // Maintain original order
            newBlocks.sort((a, b) => {
                if (a.paraId !== b.paraId) return a.paraId - b.paraId;
                return a.textId - b.textId;
            });

            console.log("Updated blocks with empty strings:", newBlocks);
            return newBlocks;
        });

        // Rest of your history update logic
        const isGroupedByPara = groupedSourceSentences.length !== groupedTranslatedSentences.length;
        const sourceBlocks = normalizedGroups.source[index] || [];
        const sourceText = sourceBlocks.map(b => b.text).join(' ');
        const originalTranslatedBlocks = normalizedGroups.translated[index] || [];
        const originalTranslatedText = originalTranslatedBlocks.map(b => b.text).join(' ');
        const updatedText = updatedBlocks.map(b => b.text).join(' ');

        setEditedHistory(prev => {
            const key = isGroupedByPara
                ? `para-${updatedBlocks[0]?.paraId}`
                : `sentence-${updatedBlocks[0]?.paraId}-${updatedBlocks[0]?.sentId}`;

            return {
                ...prev,
                [key]: {
                    source: sourceText,
                    translated: originalTranslatedText,
                    updated: updatedText
                }
            };
        });
    }, [groupedSourceSentences.length, groupedTranslatedSentences.length, normalizedGroups]);

    const handleSubmit = () => {
        setLoading(true);
        // Get values from editedHistory object
        const updatedParts = Object.values(editedHistory);

        if (updatedParts.length > 0) {
            dispatch(updatePDFFileAction({
                documentUrl: translatedFileUrl,
                updatedTextBlocks: docxTextBlocks,
                updatedParts,
                outputPath: `${job_id}.pdf`,
                job_id: job_id
            }, onSuccess, onError));
        } else {
            navigate('/post-edit/summary', { state: { job_id: job_id } });
        }
    };

    const handleSaveDraft = () => {
        setLoading(true);

        const updatedParts = Object.values(editedHistory);

        dispatch(updatePDFFileAction({
            documentUrl: translatedFileUrl,
            updatedTextBlocks: docxTextBlocks,
            updatedParts,
            outputPath: `${job_id}.pdf`,
            job_id: job_id
        }, onSaveSuccess, onError));
    };

    const onSuccess = (data) => {
        setLoading(false);
        successToast(data.data.message);
        const job = data.data.data;
        const updatedParts = Object.values(editedHistory);
        navigate('/post-edit/summary', {
            state: {
                updatedParts: updatedParts,
                sourceLang: job.source_lang,
                targetLang: job.target_lang,
                job_id: job_id
            }
        });
        setEditedHistory({});
    };

    const onSaveSuccess = (data) => {
        setLoading(false);
        setEditedHistory({});
        successToast(data.data.message);
        dispatch(getPDFTextBlocksAction({ job_id, sourceFileUrl }, onFetchSuccess, onError));
    };

    const onError = (data) => {
        setLoading(false);
        errorToast(data?.data?.message);
    };

    return (
        <div className='bg-white h-100 border rounded-4 m-2'>
            <div className='d-flex justify-content-between w-100 align-items-center p-2'>
                <div className='d-flex gap-4 w-100 align-items-center'>
                    <CustomButton
                        disabled={true}
                        variant='primary'
                        className='d-flex align-items-center cursor_pointer fs_12'
                        style={{ height: '30px' }}
                    >
                        {availableLanguages.find(lang => lang?.value === documentData?.source_lang)?.label ?? "Loading..."}
                    </CustomButton>
                </div>
                <div className='d-flex justify-content-between gap-4 w-100 align-items-center'>
                    <div className='px-3'>
                        <CustomButton
                            disabled={true}
                            variant='primary'
                            className='d-flex align-items-center cursor_pointer fs_12'
                            style={{ height: '30px' }}
                        >
                            {availableLanguages.find(lang => lang?.value === documentData?.target_lang)?.label ?? "Loading..."}
                        </CustomButton>
                    </div>
                    <div className='d-flex gap-3'>
                        <CustomButton variant='primary' disabled={loading} className='d-flex align-items-center fs_12' style={{ height: '30px' }} onClick={handleSaveDraft}>
                            Save Draft
                        </CustomButton>
                        <CustomButton variant='primary' disabled={loading} className='d-flex align-items-center fs_12' style={{ height: '30px' }} onClick={handleSubmit}>
                            Submit For Review
                        </CustomButton>
                    </div>
                </div>
            </div>

            <div className='border-top' style={{ height: "80vh", overflowY: "auto" }}>
                {(isLoading || loading) ? (
                    <div className="d-flex justify-content-around align-items-center" style={{ height: "100%" }}>
                        <Spinner animation="border" variant="primary" />
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <Table borderless className="mb-0" style={{
                        borderCollapse: 'separate',
                        borderSpacing: '5px',
                        tableLayout: 'fixed',  // Add this
                        width: '100%'         // Add this
                    }}>
                        <tbody>
                            {normalizedGroups.source.map((sourceBlock, index) => {
                                const translatedBlock = normalizedGroups.translated[index] || [];
                                return (
                                    <tr key={`row-${index}`}>
                                        <td style={{ width: '50%', verticalAlign: "top", border: "1px solid #dee2e6", backgroundColor: selectedId === index ? "rgb(236, 243, 251)" : "#ffff" }}>
                                            <PdfSourceTextBlock
                                                key={`source-${sourceBlock[0].paraId}-${sourceBlock[0].sentId}`}
                                                block={sourceBlock}
                                            />
                                        </td>
                                        <td
                                            style={{
                                                width: '50%',  // Changed from '50wh' to '50%'
                                                verticalAlign: "top",
                                                border: "1px solid #dee2e6",
                                                backgroundColor: selectedId === index ? "rgb(236, 243, 251)" : "#ffff",
                                                maxWidth: '50%',  // Added to ensure it doesn't overflow
                                                wordBreak: 'break-word'  // Ensure text breaks properly
                                            }}
                                            onClick={() => setSelectedId(index)}
                                        >
                                            {translatedBlock.length > 0 ? (
                                                <PdfTextBlock
                                                    index={index}
                                                    key={`translated-${index}`}
                                                    onUpdate={handleTextBlockUpdate}
                                                    block={translatedBlock}
                                                />
                                            ) : (
                                                <div style={{ minHeight: '40px' }}></div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </div>
        </div>
    );
};

export default PdfEditor;